import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Users } from 'lucide-react';

const PrivacyPage = () => {
  const principles = [
    {
      icon: Shield,
      title: 'Minimal Data',
      description: 'We collect only what\'s necessary to offer mentorship and improve learning.'
    },
    {
      icon: Eye,
      title: 'No Selling',
      description: 'We never sell, rent, or trade your data. Ever.'
    },
    {
      icon: Lock,
      title: 'Secure',
      description: 'Your information is stored with industry-standard protection.'
    },
    {
      icon: Users,
      title: 'Transparent',
      description: 'We tell you exactly what we collect and why.'
    }
  ];

  const sections = [
    {
      title: '1. What Information We Collect',
      content: `We believe in minimalism — both in physics and privacy.

Here's what we may collect, and why:

a. Basic Information
Your name, email, and WhatsApp number (only if you message or fill out a mentorship form).
This helps us reach out for mentorship discussions, feedback, and updates.

b. Communication Data
Questions, feedback, or doubts you send us — we keep them to maintain mentorship continuity and track learning progress.

c. Usage Data
Non-personal information like pages visited, buttons clicked, or duration of visit.
This helps us understand how students use the platform and improve the experience.

We Do NOT Collect:
• Your exact GPS location.
• Payment card details (handled securely by third-party gateways if used).
• Any personal data not related to mentorship or learning.`
    },
    {
      title: '2. How We Use Your Data',
      content: `We use the information you share to:
• Provide personalized mentorship and learning support.
• Respond to your messages and guide your progress.
• Improve our content, tools, and overall student experience.
• Occasionally send important updates (rare, thoughtful, and relevant).

We do not:
• Sell or rent your data to anyone.
• Use it for irrelevant marketing.
• Track you across other websites.`
    },
    {
      title: '3. Cookies and Analytics',
      content: `We use small files called cookies (sadly, not chocolate chip) to:
• Remember you between visits.
• Understand how students interact with our site (through tools like Google Analytics).

You can disable cookies in your browser at any time. Most of the site will still function normally.`
    },
    {
      title: '4. Data Security',
      content: `We take your privacy seriously.

Your data is:
• Stored on secure servers located in Pune, Maharashtra, India.
• Protected with encryption and access restrictions.
• Regularly backed up for reliability.

However, no system is 100% immune. If you ever spot a security issue or suspect misuse, please contact us immediately at aditya@physicsnotebook.in.`
    },
    {
      title: '5. Data Retention and Deletion',
      content: `• We keep your data only as long as it serves educational or mentorship purposes.
• You can request deletion at any time — no forms, no red tape. Just email or message us.
• Once verified, your data will be permanently deleted within 15 working days.`
    },
    {
      title: '6. Third-Party Services',
      content: `We occasionally use trusted external services, such as:
• Google Analytics – to understand anonymous usage trends.
• WhatsApp – for direct mentorship and communication.
• Razorpay / Stripe (if applicable) – to process payments securely.

Each of these services has its own privacy policies, and we ensure they maintain strong data protection standards.`
    },
    {
      title: '7. Children\'s Privacy',
      content: `PhysicsNotebook.in is designed for students aged 13 and above.

If you're younger than 13, please use the site under the supervision of a parent, guardian, or teacher.`
    },
    {
      title: '8. Your Rights',
      content: `You always have control over your data. You can:
• Request a copy of your stored data.
• Ask for corrections or updates.
• Request deletion of your data from our systems.

Just reach out via email or WhatsApp — no legal jargon or complicated forms needed.`
    },
    {
      title: '9. Updates to This Policy',
      content: `As PhysicsNotebook.in grows, we may revise this policy to stay compliant and transparent.

If significant changes are made, we'll notify users on the website.

Please check this page occasionally for the latest version.`
    },
    {
      title: '10. Contact Us',
      content: `Questions, concerns, or compliments? We're happy to help.

📧 Email: aditya@physicsnotebook.in
📱 WhatsApp: +91 9309242445
🌐 Website: www.physicsnotebook.in
📍 Location: Pune, Maharashtra, India`
    },
    {
      title: 'In Short',
      content: `We're here to mentor, not to monitor.

Your data helps us guide you better — and you always stay in control of it.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy - PhysicsNotebook.in</title>
        <meta name="description" content="Learn how we protect your privacy and handle your data at PhysicsNotebook.in. Your trust is important to us." />
      </Helmet>

      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Effective Date: November 2025<br />
              Last Updated: November 2025
            </p>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              At PhysicsNotebook.in, your privacy matters. We respect it, protect it, and never misuse it — because we're mentors, not marketers.
            </p>
          </motion.div>

          {/* Privacy Principles */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Promise to You</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We keep it simple, secure, and student-focused.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {principles.map((principle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center"
              >
                <div className="bg-blue-50 p-3 rounded-full mb-4">
                  <principle.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{principle.title}</h3>
                <p className="text-gray-600">{principle.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-gray-100">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg mb-10 last:mb-0"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
